import React, { useContext, useEffect } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHeart,
    faList,
    faStar,
    faMessage,
} from '@fortawesome/pro-regular-svg-icons';
import {
    POL_THEME,
    MCO_THEME,
    ThemeContext,
} from '../../constants/theme';
import styles from './About.scss';
import themeStyles from '../../_theme.scss';
import Button from '../shared/Button/Button';
import { Titles } from '../../constants/titles';
import { AppContext } from '../../context/AppContext';
import { Descriptions } from '../../constants/Descriptions';

// Background Images
const POL_ABOUT_IMG = `${process.env.CUSTOMER_DATA_S3_URL}/about-us/pol_about-us_bg.jpg`;
const MCO_ABOUT_IMG = `${process.env.CUSTOMER_DATA_S3_URL}/about-us/mco_about-us_bg.jpg`;

const About = () => {
    const themeContext = useContext(ThemeContext);
    const { setPageTitle, setPageDescription } = useContext(AppContext);

    useEffect(() => {
        switch (themeContext) {
            case MCO_THEME:
                setPageTitle(Titles.MCO_ABOUT);
                setPageDescription(Descriptions.MCO_ABOUT);
                break;
            case POL_THEME:
                setPageTitle(Titles.POL_ABOUT);
                setPageDescription(Descriptions.POL_ABOUT);
                break;
            default:
                break;
        }
    }, [themeContext, setPageTitle, setPageDescription]);

    let headlineColor = '';
    let connectBackground = '';
    let marketingHeadline = '';
    let marketingCardBody = '';
    let featuresHeadline = '';
    let findPubsHeadline = '';
    let findPubsBody = '';
    let panelHeadlineStyle = '';
    let panelBodyStyle = '';
    let panelTextSection = '';
    let broughtBodyCopy = '';
    let siteURL = '';
    let siteURLPretty = '';

    if (themeContext === POL_THEME) {
        headlineColor = styles.headlineColorPOL;
        connectBackground = styles.polConnectBackground;
        panelHeadlineStyle = styles.polPanelHeadline;
        panelBodyStyle = styles.polPanelBody;
        panelTextSection = styles.polPanelTextSection;
        marketingHeadline = (
            <h5 className={classNames(styles.panelPrimary, panelHeadlineStyle)}>
                Connect with
                your local church or business
            </h5>
        );
        marketingCardBody = (
            <p className={classNames(styles.panelSecondary, panelBodyStyle)}>
                Parishes Online is your
                source for church bulletins, and the advertisers that support them.
            </p>
        );
        findPubsHeadline = <h3 className={styles.headerText}>Find your church publications</h3>;
        featuresHeadline = <h3 className={styles.parishHeader}>Parishes Online Features</h3>;
        findPubsBody = (
            <div>
                <p className={styles.boldParagraph}>
                    Staying connected with your local church has never been easier.
                    Simply visit the listing to download latest bulletins, publications, or newsletters.
                </p>
                <p className={styles.boldParagraph}>
                    Sign up for instant e-mail updates from your church. You’ll receive an e-mail notification
                    when the newest publication is ready to be viewed, with a link to view it online.
                    Stay informed and never miss a thing with Parishes Online.
                </p>
            </div>
        );
        broughtBodyCopy = (
            <div>
                <p>
                    For over 40 years, LPi has served local businesses, churches, and other organizations.
                    In addition to printing church bulletins and newsletters, we use the latest technology
                    to help you connect with your community.
                </p>
                <p>
                    Our mission is to Connect Your Community.
                    We&apos;ve been helping connect communities since 1972.
                    We work to achieve our mission by adhering to
                    a set of core values. These values help us achieve
                    our vision of efficiently providing superior value
                    and customer service to communities around the country.
                </p>
            </div>
        );
        siteURL = 'https://www.4LPi.com';
        siteURLPretty = 'www.4LPi.com';
    } else {
        headlineColor = styles.headlineColorMCO;
        connectBackground = styles.mcoConnectBackground;
        panelHeadlineStyle = styles.mcoPanelHeadline;
        panelBodyStyle = styles.mcoPanelBody;
        panelTextSection = styles.mcoPanelTextSection;
        marketingHeadline = (
            <h5 className={classNames(styles.panelPrimary, panelHeadlineStyle)}>
                Your source for
                connecting with your local community.
            </h5>
        );
        marketingCardBody = (
            <p className={classNames(styles.panelSecondary, panelBodyStyle)}>
                My Community Online
                helps connect you with your local senior center,
                community center, municipality, and other organizations —
                and the local businesses that support them.
            </p>
        );
        findPubsHeadline = <h3 className={styles.headerText}>Find your community publications</h3>;
        featuresHeadline = <h3 className={styles.parishHeader}>My Community Online Features</h3>;
        findPubsBody = (
            <div>
                <p className={styles.boldParagraph}>
                    Staying connected with your local organization has never been easier.
                    Simply visit the listing to download latest publications, newsletters, and more
                </p>
                <p className={styles.boldParagraph}>
                    Sign up for instant e-mail updates from your vibrant center.
                    You’ll receive an e-mail notification when the newest publication is ready to be viewed,
                    with a link to view it online. Stay informed and never miss a thing with My Community Online.
                </p>
            </div>
        );
        broughtBodyCopy = (
            <div>
                <p>
                    For over 40 years, LPi has served local businesses and other organizations.
                    In addition to printing community newsletters, we use the latest technology
                    to help you connect with your community.
                </p>
                <p>
                    Since 1972, our mission is to Connect Your Community.
                    We work to achieve our mission by adhering to a set of core values.
                    These values help us achieve our vision of efficiently providing
                    superior value and customer service to communities around the country.
                </p>
            </div>
        );
        siteURL = 'https://www.lpicommunities.com/';
        siteURLPretty = 'www.lpicommunities.com.';
    }

    let findBusinessesBody = '';
    if (themeContext === POL_THEME) {
        findBusinessesBody = (
            <div>
                <p className={styles.boldParagraph}>
                    Every business listed with Parishes Online supports a local church by sponsoring
                    an advertisement on their bulletin, newsletter, or website.
                </p>
                <p className={styles.boldParagraph}>
                    From plumbers and electricians, to restaurants and retail shops, Parishes Online helps you discover,
                    rate and review these businesses. We make it easy to find local businesses,
                    and you can feel good knowing you’re supporting a member of your community.
                </p>
            </div>
        );
    } else {
        findBusinessesBody = (
            <div>
                <p className={styles.boldParagraph}>
                    Every business listed with My Community Online supports a local center by
                    sponsoring an advertisement on their newsletter or website.
                </p>
                <p className={styles.boldParagraph}>
                    From plumbers and electricians, to restaurants and retail shops,
                    My Community Online helps you discover,
                    rate and review these businesses. We make it easy to find local businesses,
                    and you can feel good knowing you’re supporting a member of your community.
                </p>
            </div>
        );
    }

    let loyaltyBody = '';
    if (themeContext === POL_THEME) {
        loyaltyBody = (
            <div>
                <p>
                    Most of our visitors are loyal church members who seek businesses they can trust.
                    By listing with Parishes Online, you&apos;re joining
                    a network of other trustworthy businesses like yours.
                </p>
            </div>
        );
    } else {
        loyaltyBody = (
            <div>
                <p>
                    Most of our visitors are loyal community members who seek businesses they can trust.
                    By listing with My Community Online, you&apos;re joining a
                    network of other trustworthy businesses like yours.
                </p>
            </div>
        );
    }

    let adInterestBody = '';
    if (themeContext === POL_THEME) {
        adInterestBody = (
            <div>
                <p>
                    Parishes Online is a free service for existing print
                    advertisers with LPi. Find out how easy it is advertise with Parishes Online.
                </p>
            </div>
        );
    } else {
        adInterestBody = (
            <div>
                <p>
                    My Community Online is a free service for existing
                    print advertisers with LPi. Find out how easy it is to advertise with My Community Online.
                </p>
            </div>
        );
    }

    return (
        <div className={styles.outerContainer}>
            <main className={styles.container}>
                <header className={styles.headerWrapper}>
                    <h1 className={`${styles.primaryHeader} ${themeStyles.headerText}`}>About Us</h1>
                    <div className={styles.learnMoreWrapper}>
                        Want to learn more?
                        <a
                            href="https://www.4lpi.com/contact-us"
                            rel="noreferrer"
                            target="_blank"
                            className={styles.infoButton}
                        >
                            <Button size="small">
                                Contact us
                            </Button>
                        </a>
                    </div>
                </header>
                <section
                    className={classNames(styles.mainPanel, connectBackground)}
                    style={{
                        backgroundImage: themeContext === POL_THEME
                            ? `url('${POL_ABOUT_IMG}')`
                            : `url('${MCO_ABOUT_IMG}')`,
                    }}
                >
                    <div className={classNames(styles.panelTextWrapper, panelTextSection)}>
                        {marketingHeadline}
                        {marketingCardBody}
                    </div>
                    <div className={styles.column2} />
                    <div className={styles.column3} />
                </section>
                <section className={styles.infoSection}>
                    <div className={styles.leftColumn}>
                        {findPubsHeadline}
                        {findPubsBody}
                    </div>

                    <div className={styles.rightColumn}>
                        <h3 className={styles.headerText}>Find local businesses you can trust</h3>
                        {findBusinessesBody}
                    </div>
                </section>
                <section className={styles.parishSection}>
                    {featuresHeadline}
                </section>

                <section className={styles.featuresSection}>
                    <div className={styles.infoColumn}>
                        <h5 className={classNames(styles.moreFeatures, headlineColor)}>
                            <FontAwesomeIcon size="2x" icon={faHeart} className={styles.icon} />
                            Customer Loyalty
                        </h5>
                        {loyaltyBody}
                    </div>
                    <div className={styles.infoColumn}>
                        <h5 className={classNames(styles.moreFeatures, headlineColor)}>
                            <FontAwesomeIcon size="2x" icon={faList} className={styles.icon} />
                            Business Info
                        </h5>
                        <p>
                            Guide customers to your business with free features like maps,
                            business hours, a business description, a link to your company website,
                            and other contact information.
                        </p>
                    </div>
                    <div className={styles.infoColumn}>
                        <h5 className={classNames(styles.moreFeatures, headlineColor)}>
                            <FontAwesomeIcon size="2x" icon={faStar} className={styles.icon} />
                            Ratings and Reviews
                        </h5>
                        <p>
                            Users can easily rate and review their favorite businesses.
                            These reviews are featured on your profile, helping you stand out
                            above the crowd.
                        </p>
                    </div>
                    <div className={styles.infoColumn}>
                        <h5 className={classNames(styles.moreFeatures, headlineColor)}>
                            <FontAwesomeIcon size="2x" icon={faMessage} className={styles.icon} />
                            Coupons
                        </h5>
                        <p>
                            Engage your customers using our free coupon generator.
                            Create coupons and start driving customers to your business.
                        </p>
                    </div>
                </section>
                <section className={styles.moreInfoSection}>
                    <div className={styles.bottomLeftColumn}>
                        <h5 className={classNames(styles.bottomLeftColumnHeader, headlineColor)}>
                            Brought to you by LPi
                        </h5>
                        {broughtBodyCopy}
                        <p>
                            For more information about LPi, our products, services,
                            and Web solutions,
                            please visit&#160;
                            <a href={siteURL} rel="noreferrer" target="_blank">{siteURLPretty}</a>
                            .
                        </p>
                    </div>
                    <div className={styles.bottomRightColumn}>
                        <h5 className={classNames(styles.bottomRightColumnHeader, headlineColor)}>
                            Interested in advertising?
                        </h5>
                        {adInterestBody}
                        <a
                            rel="noreferrer"
                            target="_blank"
                            href="https://www.4lpi.com/contact-us/"
                        >
                            <Button size="small">
                                Contact us
                            </Button>
                        </a>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default About;
